import React from "react";

const AboutUs = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white via-gray-50 to-blue-50 text-gray-900">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-gray-100 py-5 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Americar Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-semibold font-poppins text-blue-700">
              About Americar
            </span>
          </div>

          {/* Back to Home */}
          <a
            href="/"
            className="text-blue-600 hover:text-blue-800 font-medium transition-all"
          >
            ← Back to Home
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-5xl mx-auto px-6 py-16 sm:py-24 space-y-16">
        {/* Hero / Intro */}
        <section className="text-center">
          <h1 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mb-6">
            Redefining Car Care for the Modern Driver
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            <strong>Americar</strong> is a mobility technology platform built to make car
            ownership simple, connected, and worry-free. We help drivers manage
            maintenance, connect with trusted vendors, and access roadside
            assistance — all from a single app. Whether you’re a driver or a
            service provider, Americar bridges technology and trust to keep
            everyone moving forward.
          </p>
        </section>

        {/* Mission Section */}
        <section className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-gray-900 mb-4">
            Our Mission
          </h2>
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            Our mission is to make vehicle ownership effortless. From routine
            maintenance to emergency assistance, Americar provides a seamless
            experience powered by real-time tracking, verified vendors, and
            intuitive design.  
            <br />
            <br />
            We believe every driver deserves confidence and convenience — no
            hidden costs, no unreliable garages, just peace of mind on every
            journey.
          </p>
        </section>

        {/* What We Do */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-gray-900 mb-8 text-center">
            What We Do
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                emoji: "🚗",
                title: "For Drivers",
                desc: "Easily track your car’s health, schedule maintenance, and access emergency assistance whenever needed.",
              },
              {
                emoji: "🧰",
                title: "For Vendors",
                desc: "Get verified service requests, manage customers, and grow your automotive business through our vendor platform.",
              },
              {
                emoji: "⚡",
                title: "Technology-Driven",
                desc: "Real-time tracking, digital records, and AI-based insights to simplify every step of vehicle management.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all text-center"
              >
                <div className="text-4xl mb-3">{item.emoji}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-poppins">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Values Section */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-gray-900 mb-8 text-center">
            Our Core Values
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 text-center">
            {[
              {
                emoji: "🤝",
                title: "Trust First",
                desc: "We prioritize safety and reliability by verifying every vendor and ensuring transparency in all interactions.",
              },
              {
                emoji: "💡",
                title: "Innovation with Purpose",
                desc: "We use technology not just to simplify, but to solve real problems faced by drivers and vendors daily.",
              },
              {
                emoji: "🌍",
                title: "Community-Driven",
                desc: "Our platform connects local service providers with drivers, supporting small businesses and better experiences.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all"
              >
                <div className="text-4xl mb-3">{item.emoji}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-poppins">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Get In Touch */}
        <section className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-gray-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-gray-700 text-base sm:text-lg mb-4 max-w-2xl mx-auto">
            Have questions, feedback, or partnership ideas?  
            We’d love to hear from you.
          </p>
          <p className="text-gray-700 text-base sm:text-lg">
            📧{" "}
            <a
              href="mailto:vroom@americar.app"
              className="text-blue-600 hover:underline font-medium"
            >
              vroom@americar.app
            </a>
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center">
        <p className="text-sm">
          © 2025 Americar. All rights reserved. | Built with ❤️ by the Americar
          Team
        </p>
      </footer>
    </div>
  );
};

export default AboutUs;
